import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useToastContext } from '../../../../../../../contexts'
import { DeliveryForecastDespatchAdvicePackagingMaterial } from '../../../../../../../data/models'
import { useMode } from '../../../../../../../hooks'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import {
  requestCreatePackagingMaterial,
  requestUpdatePackagingMaterial,
  usePackagingMaterialById,
} from '../../../data'
import { FormValues } from '../common'
import { EditPackagingDataForm } from './EditPackagingData.form'

export const EditPackagingDataData: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showToast } = useToastContext()
  const { packagingId } = useParams()
  const { data: packaging } = usePackagingMaterialById(packagingId)

  const mode = useMode()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const dtoFromFormData = (
    formData: FormValues,
  ): DeliveryForecastDespatchAdvicePackagingMaterial => {
    return {
      Id: packagingId,
      PackagingDataId: formData.packagingDataId!,
      Description: formData.description!,
      PackagingMaterial: formData.packagingMaterial,
      Measurements: {
        Length: formData.length,
        Width: formData.width,
        Height: formData.height,
        Unit: formData.measurementsUnit,
      },
      TareWeight: {
        Value: formData.tareWeight,
        Unit: formData.tareWeightUnit,
      },
      Volume: {
        Value: formData.volume,
        Unit: formData.volumeUnit,
      },
      PackagingIdentifier: formData.packagingIdentifier,
      PropertyIdentifier: formData.propertyIdentifier,
      IsStackable: formData.isStackable,
    }
  }

  const handleSubmit = async (formData: any) => {
    try {
      setIsRequestRunning(true)

      if (mode === 'add')
        await requestCreatePackagingMaterial(dtoFromFormData(formData))
      if (mode === 'edit')
        await requestUpdatePackagingMaterial(
          packagingId!,
          dtoFromFormData(formData),
        )

      const summary =
        mode === 'add'
          ? t(
              'masterDataManagement.packagingData.table.add.dialog.add.messages.success.summary',
            )
          : t(
              'masterDataManagement.packagingData.table.edit.dialog.save.messages.success.summary',
            )
      const detail =
        mode === 'add'
          ? t(
              'masterDataManagement.packagingData.table.add.dialog.add.messages.success.detail',
              {
                description: formData?.description,
              },
            )
          : t(
              'masterDataManagement.packagingData.table.edit.dialog.save.messages.success.detail',
              {
                description: formData?.description,
              },
            )

      showToast({
        severity: 'success',
        summary: summary,
        detail: detail,
      })

      handleClose()
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message
      const summary =
        mode === 'add'
          ? t(
              'masterDataManagement.packagingData.table.add.dialog.add.messages.failure.summary',
            )
          : t(
              'masterDataManagement.packagingData.table.edit.dialog.save.messages.failure.summary',
            )

      showToast({
        severity: 'error',
        summary: summary,
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const handleClose = () => navigate('/master-data-management/packaging-data')

  return (
    <EditPackagingDataForm
      handleSubmit={handleSubmit}
      isRequestRunning={isRequestRunning}
      handleClose={handleClose}
      packaging={packaging}
    />
  )
}
